import PropTypes from 'prop-types';
import { Box, CircularProgress, DialogContentText, useMediaQuery } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useEffect, useState, useMemo } from 'react';
import { TICKET_ORDERS } from '../../../../../../reusable-components/datagrid/orderColumns';
import { useSnackbar } from '../../../../../../reusable-components/snackbar';
import { getOrderItemsForTicket, getRentalOrderItemsForTicket } from '../../../../../../api/tickets';
import TicketOrderSearch from './TicketOrderSearch';
import OrderItemsMobileView from './OrderItemsMobileView';
import { getRentalOrderStatus } from '../../../../../../api/rentals';

OrderSearchWithItemTable.propTypes = {
  group: PropTypes.object,
  field: PropTypes.object,
  setFieldValue: PropTypes.func,
  facilityId: PropTypes.number,
};

export default function OrderSearchWithItemTable({ field, facilityId, setFieldValue, group })
{
  const { enqueueSnackbar } = useSnackbar();

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderItems, setOrderItems] = useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [rentalOrderStatus, setRentalOrderStatus] = useState(null);
  
  const orderLink = useMemo(() => {
    if(selectedOrder?.orderType === 'Rental') {
      return `<a href="/dashboard/rentals/rental-order/${selectedOrder.id}/${rentalOrderStatus}">${selectedOrder.label}</a><p> </p>`; 
    }
    if(selectedOrder?.orderType === 'Purchase') {
      return `<a href="/dashboard/orders/${selectedOrder.id}">${selectedOrder.label}</a><p> </p>`;
    }
    return '';
  }, [selectedOrder, rentalOrderStatus]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getRentalOrderStatus(selectedOrder?.id);
        setRentalOrderStatus(response.data);
      } catch (error) {
        enqueueSnackbar('Failed to fetch rental order status', { variant: 'error' });
      }
    })();
  }, [selectedOrder]);
  
  useEffect(() => {
    setOrderItems(null);
    if (selectedOrder?.orderType === "Rental") {
      getRentalOrderItemsForTicket(selectedOrder.id, isMobile)
        .then(res => setOrderItems(res.data.map((item, index) => ({ ...item, id: index }))))
        .catch(() => {
          enqueueSnackbar('Failed to get order items', { variant: 'error' });
          setOrderItems([]);
        });
    }
    else if (selectedOrder) {
      getOrderItemsForTicket(selectedOrder.id, isMobile)
        .then(res => setOrderItems(res.data.map((item, index) => ({ ...item, id: index }))))
        .catch(() => {
          enqueueSnackbar('Failed to get order items', { variant: 'error' });
          setOrderItems([]);
        });
    }
  }, [selectedOrder])

  const handleRowSelection = (newSelection) => {
    const itemList = orderItems.filter(item => newSelection.includes(item.id)).map(item => `<li>${item.psNumber ?
      `<a href="/dashboard/admin/catalog/${item.psNumber.replace("PS", "")}">${item.psNumber}</a> - ` : ``}` +
      `${item.description} | Qty: ${item.quantity}</li>`).join("");
    setFieldValue(field, group, {
      value: newSelection,
      htmlValue: selectedOrder ? `<p>Hi, I need assistance with these items on ${orderLink}<ul>${itemList}</ul></p>`: "",
      subjectValue: selectedOrder?.orderNumber,
      addUser: selectedOrder?.submittedBy,
      orderId: selectedOrder?.id,
      orderType: selectedOrder?.orderType,
    });     
  }
  
  return (
    <Box>
      <TicketOrderSearch facilityId={facilityId} onChange={(order) => setSelectedOrder(order)} />
      {orderItems ? <>
        <DialogContentText sx={{ mt: 3 }}>
          Select items from order
        </DialogContentText>
        <Box sx={{ height: orderItems.length * 44 + 88 > 300 ? 300 : orderItems.length * 44 + 88, width: '100%', mb: 3 }}>
          {isMobile ?
          <OrderItemsMobileView
            orderItems={orderItems}
            handleRowSelection={handleRowSelection}
          /> :
          <DataGridPro
            getRowHeight={() => 'auto'}
            rows={orderItems}
            columns={selectedOrder?.orderType === 'Rental' ? TICKET_ORDERS.slice(0, 2) : TICKET_ORDERS}
            checkboxSelection
            getRowId={(row) => row?.id}
            disableSelectionOnClick
            rowHeight={44}
            hideFooter
            onRowSelectionModelChange={handleRowSelection}
            rowSelectionModel={field.value || []}
          />}
        </Box>
      </> : selectedOrder  ? <Box sx={{ textAlign: "center", mt: 2 }}><CircularProgress sx={{ alignSelf: "center" }} /></Box> : ""}
    </Box>
  );
}